import React, { useId } from "react";
import PropTypes from "prop-types";
import cls from "classnames";
import styles from "./BookingCard.module.css";
import { Icon, formatRangeLabel } from "@zorni/components-lib";
import { currentLocale } from "../../../../common/i18n/locale";

const resolveCopy = (t, key, fallback) => {
  const value = t?.(key);
  return !value || value === key ? fallback : value;
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const [y, m, d] = String(value).slice(0, 10).split("-").map(Number);
  if (y && m && d) return new Date(y, m - 1, d);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/* « 8 900 MAD ». Montant absent → null (la carte affiche un tiret). */
export const formatAmount = (amount, locale = currentLocale()) => {
  const value = Number(amount?.value);
  if (amount?.value === null || amount?.value === undefined || Number.isNaN(value)) return null;
  const number = value.toLocaleString(locale, { maximumFractionDigits: 2 });
  return amount?.currency ? `${number} ${amount.currency}` : number;
};

const TRAVELER_ROWS = [
  { key: "adults", label: ["components.booking.adults", "Adultes"], hint: ["components.booking.adultsHint", "12 ans et plus"] },
  { key: "children", label: ["components.booking.children", "Enfants"], hint: ["components.booking.childrenHint", "2 à 11 ans"] },
  { key: "babies", label: ["components.booking.babies", "Bébés"], hint: ["components.booking.babiesHint", "Moins de 2 ans"] },
];

/**
 * BookingCard — carte de réservation du détail d'un voyage (portal : liée à
 * la réservation, utilisée sur une seule page)
 * (.booking-card de « 03 - tripDetails »).
 *
 *   PRIX / PERSONNE                         [badge]
 *   8 900 MAD  base chambre double
 *   DATE DE DÉPART   [23 – 30 nov. · 8 900 MAD] [22 – 29 mars …]
 *   VOYAGEURS        Adultes  − 2 +   Enfants − 0 +   Bébés − 0 +
 *   2 × 8 900 MAD ………………… 17 800 MAD
 *   Total …………………………………… 18 300 MAD
 *   [ Réserver ce voyage · note            → ]
 *
 * Composant d'affichage : le parent fournit les dates, les voyageurs, les
 * lignes de prix (calculées par l'API) et réagit aux choix. `loading`
 * estompe les montants pendant un nouveau calcul.
 */
export function BookingCard({
  price,
  priceLabel,
  priceNote,
  badge,
  badgeIcon = "iconPlane",
  dates = [],
  selectedDateId,
  onSelectDate,
  travelers = { adults: 1, children: 0, babies: 0 },
  onTravelersChange,
  travelerLimits = { adults: { min: 1, max: 20 }, children: { min: 0, max: 20 }, babies: { min: 0, max: 20 } },
  hiddenTravelers = [],
  travelerHints = {},
  summary = [],
  total,
  loading = false,
  ctaLabel,
  ctaNote,
  onReserve,
  reserveDisabled = false,
  message,
  security,
  t,
  className,
}) {
  const datesLabelId = `${useId()}-dates`;
  const priceText = formatAmount(price);
  const totalText = formatAmount(total);

  const step = (key, delta) => {
    const limits = travelerLimits?.[key] || { min: 0, max: 20 };
    const current = Number(travelers?.[key]) || 0;
    const next = Math.min(limits.max ?? 20, Math.max(limits.min ?? 0, current + delta));
    if (next !== current) onTravelersChange?.({ ...travelers, [key]: next });
  };

  return (
    <div className={cls(styles.card, className)}>
      <div className={styles.header}>
        <div>
          <span className={styles.kicker}>
            {priceLabel || resolveCopy(t, "components.booking.pricePerPerson", "Prix / personne")}
          </span>
          <div className={styles.price}>
            <strong>{priceText ?? "—"}</strong>
            {priceNote && <span>{priceNote}</span>}
          </div>
        </div>
        {badge && (
          <span className={styles.badge}>
            {badgeIcon && <Icon name={badgeIcon} size="xs" color="inherit" />}
            {badge}
          </span>
        )}
      </div>

      {dates.length > 0 && (
        <div className={styles.section}>
          <span className={styles.sectionLabel} id={datesLabelId}>
            {resolveCopy(t, "components.booking.departure", "Date de départ")}
          </span>
          <div className={styles.dates} role="radiogroup" aria-labelledby={datesLabelId}>
            {dates.map((d) => {
              const selected = d.id === selectedDateId;
              const range =
                formatRangeLabel(toDate(d.startDate), toDate(d.endDate)) || d.label || "";
              const sub = d.sublabel ?? formatAmount(d.price);
              return (
                <button
                  key={d.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={d.disabled}
                  className={cls(styles.dateChip, { [styles.dateChipActive]: selected })}
                  onClick={() => !selected && onSelectDate?.(d.id)}
                >
                  <strong>{range}</strong>
                  {sub && <span>{sub}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className={styles.section}>
        <span className={styles.sectionLabel}>
          {resolveCopy(t, "components.booking.travelers", "Voyageurs")}
        </span>
        <div className={styles.travelers}>
          {TRAVELER_ROWS.filter((row) => !hiddenTravelers.includes(row.key)).map((row) => {
            const label = resolveCopy(t, ...row.label);
            const value = Number(travelers?.[row.key]) || 0;
            const limits = travelerLimits?.[row.key] || { min: 0, max: 20 };
            return (
              <div className={styles.travelerRow} key={row.key}>
                <div className={styles.travelerInfo}>
                  <strong>{label}</strong>
                  <span>{travelerHints[row.key] || resolveCopy(t, ...row.hint)}</span>
                </div>
                <div className={styles.stepper}>
                  <button
                    type="button"
                    className={styles.stepBtn}
                    onClick={() => step(row.key, -1)}
                    disabled={value <= (limits.min ?? 0)}
                    aria-label={`${resolveCopy(t, "components.booking.decrease", "Retirer")} : ${label}`}
                  >
                    <Icon name="iconMinus" size="xs" color="inherit" />
                  </button>
                  <span className={styles.stepValue} aria-live="polite">
                    {value}
                  </span>
                  <button
                    type="button"
                    className={styles.stepBtn}
                    onClick={() => step(row.key, 1)}
                    disabled={value >= (limits.max ?? 20)}
                    aria-label={`${resolveCopy(t, "components.booking.increase", "Ajouter")} : ${label}`}
                  >
                    <Icon name="iconPlus" size="xs" color="inherit" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {(summary.length > 0 || total) && (
        <div className={cls(styles.summary, { [styles.loading]: loading })} aria-busy={loading || undefined}>
          {summary.map((row, i) => (
            <div className={styles.summaryRow} key={`${row.label}-${i}`}>
              <span>{row.label}</span>
              <strong>{formatAmount(row.amount) ?? "—"}</strong>
            </div>
          ))}
          {total !== undefined && (
            <div className={cls(styles.summaryRow, styles.total)}>
              <span>{resolveCopy(t, "components.booking.total", "Total estimé")}</span>
              <strong>{totalText ?? "—"}</strong>
            </div>
          )}
        </div>
      )}

      <button type="button" className={styles.reserve} onClick={onReserve} disabled={reserveDisabled}>
        <span>
          <span className={styles.reserveMain}>
            {ctaLabel || resolveCopy(t, "components.booking.reserve", "Réserver ce voyage")}
          </span>
          {ctaNote && <span className={styles.reserveNote}>{ctaNote}</span>}
        </span>
        <span className={styles.reserveArrow} aria-hidden="true">
          <Icon name="iconArrowRight" size="sm" color="inherit" />
        </span>
      </button>

      {message && <p className={styles.message}>{message}</p>}

      {security && (
        <div className={styles.security}>
          <Icon name="iconLock" size="xs" color="inherit" />
          {security}
        </div>
      )}
    </div>
  );
}

const amountShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currency: PropTypes.string,
});

BookingCard.propTypes = {
  /** Prix par personne affiché en tête */
  price: amountShape,
  priceLabel: PropTypes.string,
  /** « base chambre double » */
  priceNote: PropTypes.node,
  badge: PropTypes.node,
  badgeIcon: PropTypes.string,
  /** Départs : [{ id, startDate, endDate, price, sublabel, disabled }] */
  dates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      label: PropTypes.string,
      sublabel: PropTypes.node,
      price: amountShape,
      disabled: PropTypes.bool,
    })
  ),
  selectedDateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectDate: PropTypes.func,
  travelers: PropTypes.shape({
    adults: PropTypes.number,
    children: PropTypes.number,
    babies: PropTypes.number,
  }),
  /** onTravelersChange({ adults, children, babies }) */
  onTravelersChange: PropTypes.func,
  travelerLimits: PropTypes.object,
  /** Catégories à ne pas proposer : ["babies"] */
  hiddenTravelers: PropTypes.arrayOf(PropTypes.oneOf(["adults", "children", "babies"])),
  /** Indications sous chaque catégorie, si elles dépendent du voyage : { children: "Jusqu'à 12 ans" } */
  travelerHints: PropTypes.object,
  /** Lignes de détail : [{ label, amount }] */
  summary: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.node, amount: amountShape })),
  total: amountShape,
  /** Nouveau calcul en cours : montants estompés */
  loading: PropTypes.bool,
  ctaLabel: PropTypes.string,
  ctaNote: PropTypes.string,
  onReserve: PropTypes.func,
  reserveDisabled: PropTypes.bool,
  /** Message sous le bouton (« Aucun montant ne sera débité… ») */
  message: PropTypes.node,
  /** Ligne cadenas en pied (« Paiement sécurisé · Assistance 24/7 ») */
  security: PropTypes.node,
  t: PropTypes.func,
  className: PropTypes.string,
};

export default BookingCard;
